import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { MenuItemCategory, MenuCategory, MenuItem } from '@planner/types'

export function useMenuItemCategories() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Pobierz wszystkie kategorie dla dania
  const getCategoriesForItem = useCallback(async (menuItemId: string): Promise<MenuCategory[]> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('menu_item_categories')
        .select(`
          *,
          menu_category:menu_categories (*)
        `)
        .eq('menu_item_id', menuItemId)

      if (fetchError) throw fetchError
      
      return (data || []).map((item: MenuItemCategory & { menu_category?: MenuCategory }) => 
        item.menu_category
      ).filter((cat): cat is MenuCategory => !!cat)
    } catch (err) {
      console.error('Error fetching categories for item:', err)
      return []
    }
  }, [])

  // Pobierz wszystkie dania dla kategorii
  const getItemsForCategory = useCallback(async (categoryId: string): Promise<MenuItem[]> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('menu_item_categories')
        .select(`
          *,
          menu_item:menu_items (*)
        `)
        .eq('menu_category_id', categoryId)

      if (fetchError) throw fetchError
      
      return (data || []).map((item: MenuItemCategory & { menu_item?: MenuItem }) => 
        item.menu_item
      ).filter((item): item is MenuItem => !!item)
    } catch (err) {
      console.error('Error fetching items for category:', err)
      return []
    }
  }, [])

  // Dodaj kategorię do dania
  const addCategoryToItem = useCallback(async (
    menuItemId: string,
    categoryId: string
  ): Promise<{ error: string | null }> => {
    try {
      const { error: insertError } = await supabase
        .from('menu_item_categories')
        .insert([{
          menu_item_id: menuItemId,
          menu_category_id: categoryId,
        }] as unknown as never)

      if (insertError) throw insertError
      return { error: null }
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd' }
    }
  }, [])

  // Usuń kategorię z dania
  const removeCategoryFromItem = useCallback(async (
    menuItemId: string,
    categoryId: string
  ): Promise<{ error: string | null }> => {
    try {
      const { error: deleteError } = await supabase
        .from('menu_item_categories')
        .delete()
        .eq('menu_item_id', menuItemId)
        .eq('menu_category_id', categoryId)

      if (deleteError) throw deleteError
      return { error: null }
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd' }
    }
  }, [])

  // Sprawdź czy danie jest w kategorii
  const isItemInCategory = useCallback(async (
    menuItemId: string,
    categoryId: string
  ): Promise<boolean> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('menu_item_categories')
        .select('id')
        .eq('menu_item_id', menuItemId)
        .eq('menu_category_id', categoryId)
        .single()

      if (fetchError && fetchError.code !== 'PGRST116') throw fetchError // PGRST116 = no rows
      return !!data
    } catch (err) {
      console.error('Error checking item in category:', err)
      return false
    }
  }, [])

  return {
    loading,
    error,
    getCategoriesForItem,
    getItemsForCategory,
    addCategoryToItem,
    removeCategoryFromItem,
    isItemInCategory,
  }
}


